import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import CountdownTimer from "../components/CountdownTimer";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import Seo from "../components/Seo";
import { auctionEventSchema, breadcrumbSchema, videoObjectSchema } from "../lib/schema";
import MediaGallery from "../components/MediaGallery";
import VideoBadge from "../components/VideoBadge";

const AuctionDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { format } = useCurrency();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [bid, setBid] = useState("");
  const [proxy, setProxy] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/auctions/${id}`).then(({ data }) => setA(data));
  useEffect(() => { load(); }, [id]);

  const placeBid = async (e) => {
    e.preventDefault();
    if (!user || user === false) { nav("/login"); return; }
    setBusy(true);
    try {
      await api.post(`/auctions/${id}/bid`, { amount: parseFloat(bid), proxy_max: proxy ? parseFloat(proxy) : null });
      toast.success("Bid placed.");
      setBid(""); setProxy("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place bid");
    } finally { setBusy(false); }
  };

  if (!a) return <div className="pt-40 text-center text-white/40">Loading…</div>;
  const minNext = (a.current_bid || a.starting_bid) + a.bid_increment;

  return (
    <div data-testid="auction-detail" className="pt-28">
      <Seo
        title={`${a.title} · ${a.gemstone?.name || "Auction Lot"}`}
        description={`Live auction lot — ${a.gemstone?.name || ""}, ${a.gemstone?.carat || ""} ct ${a.gemstone?.category || ""} from ${a.gemstone?.origin || ""}. Reserve ${a.reserve_price?.toLocaleString?.() || a.reserve_price} ${a.currency || "USD"}. Verified bidders only.`}
        path={`/auctions/${a.id}`}
        image={a.gemstone?.image_url}
        ogType="event"
        jsonLd={[
          auctionEventSchema(a),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Auction House", url: "/auctions" },
            { name: a.title },
          ]),
          a.gemstone && videoObjectSchema(a.gemstone),
        ].filter(Boolean)}
      />
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="relative">
            {a.gemstone?.video?.url && (
              <VideoBadge className="absolute top-3 left-3 z-10" />
            )}
            <MediaGallery
              images={a.gemstone?.images}
              video={a.gemstone?.video}
              fallbackImage={a.gemstone?.image_url}
              fallbackGallery={a.gemstone?.gallery}
              alt={a.title}
            />
          </div>
        </div>
        <div>
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-4">{a.status === "live" ? "Live Auction" : a.status === "upcoming" ? "Upcoming" : "Ended"}</p>
          <h1 className="font-heading font-light text-5xl md:text-6xl leading-[1.05] mb-6">{a.title}</h1>
          <p className="text-white/70 mb-10 leading-relaxed">{a.gemstone?.name} — {a.gemstone?.carat} ct {a.gemstone?.category} from {a.gemstone?.origin}.</p>

          <div className="grid grid-cols-2 gap-6 border-y border-white/10 py-8 mb-10">
            <div>
              <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">Current Bid</div>
              <div className="font-heading text-4xl text-ivory">{format(a.current_bid, a.currency)}</div>
            </div>
            <div>
              <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">Reserve</div>
              <div className="font-heading text-4xl text-ivory">{format(a.reserve_price, a.currency)}</div>
            </div>
          </div>

          {a.status !== "ended" && <div className="mb-10"><CountdownTimer endsAt={a.ends_at} label={a.status === "live" ? "Closes In" : "Opens In"} /></div>}

          {a.status === "live" && (
            <>
              {(!user || user === false) && (
                <div className="bg-onyx border border-white/10 p-8 text-center" data-testid="bid-signin-cta">
                  <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-3">Sign in to bid</div>
                  <Link to="/login" className="inline-block mt-2 bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory">Sign In</Link>
                </div>
              )}
              {user && user !== false && user.kyc_status !== "approved" && user.role !== "admin" && (
                <div className="bg-onyx border border-yellow-400/40 p-8 text-center" data-testid="kyc-cta-bid">
                  <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-yellow-400 mb-3">
                    {user.kyc_status === "pending" ? "Verification Pending" : "Verification Required"}
                  </div>
                  <p className="text-white/60 max-w-md mx-auto leading-relaxed mb-5">
                    {user.kyc_status === "pending"
                      ? "Your KYC is under review. You'll be able to bid once compliance approves your submission."
                      : "OPIKJEWELS requires bidder verification under Indonesian AML/KYC standards. Complete a brief form to participate."}
                  </p>
                  {user.kyc_status !== "pending" && (
                    <Link to="/kyc" data-testid="get-verified-cta" className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory">
                      Get Verified to Bid
                    </Link>
                  )}
                </div>
              )}
              {user && user !== false && (user.kyc_status === "approved" || user.role === "admin") && (
                <form onSubmit={placeBid} className="bg-onyx border border-gold/30 p-8 space-y-5" data-testid="bid-form">
                  <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold">Place a Bid</div>
                  <div className="text-white/50 text-sm">Minimum next bid: <span className="text-ivory font-heading text-lg">{format(minNext)}</span></div>
                  <input data-testid="bid-amount-input" required type="number" min={minNext} step={a.bid_increment} value={bid} onChange={(e) => setBid(e.target.value)} placeholder={`${minNext}`} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold font-heading text-2xl" />
                  <input data-testid="proxy-amount-input" type="number" value={proxy} onChange={(e) => setProxy(e.target.value)} placeholder="Proxy max (optional)" className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
                  <button data-testid="bid-submit-btn" disabled={busy} className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50">
                    {busy ? "Placing…" : "Submit Bid"}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="mt-10">
            <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Bid History</div>
            <div className="space-y-2">
              {a.bids?.length ? a.bids.map((b) => (
                <div key={b.id} className="flex justify-between text-sm border-b border-white/5 py-3">
                  <span className="text-white/70">{b.user_name}</span>
                  <span className="font-heading text-ivory">{format(b.amount, a.currency)}</span>
                  <span className="text-white/40 text-xs">{new Date(b.placed_at).toLocaleString()}</span>
                </div>
              )) : <div className="text-white/40 text-sm">No bids yet.</div>}
            </div>
          </div>
        </div>
      </section>
      <div className="h-32"></div>
    </div>
  );
};

export default AuctionDetail;
